import { z } from 'zod';

const blankAsUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((value) => (value === '' ? undefined : value), schema);

export const envSchema = z.object({
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  PORT: z.coerce.number().int().positive().default(8080),

  // Database
  DATABASE_URL: z.string().url().or(z.string().startsWith('postgresql://')),

  // Railway persistent volume mount path for monthly report storage.
  // Set to /var/data/reports in production; defaults to ./data/reports locally.
  REPORTS_DIR: z.string().default('./data/reports'),

  // OAuth (GA4)
  GA4_OAUTH_CLIENT_ID: blankAsUndefined(z.string().optional()),
  GA4_OAUTH_CLIENT_SECRET: blankAsUndefined(z.string().optional()),
  GA4_OAUTH_REDIRECT_URI: blankAsUndefined(z.string().url().optional()),

  // Railway cron / scheduled job verification
  /** MainWP dashboard base URL (e.g. https://manage.ventinmedia.com) — optional */
  /** Cookie Domain for the session cookie (e.g. .support.ventinmedia.com) so
   * sibling services (workflows) share the login — optional */
  COOKIE_DOMAIN: z.string().optional(),
  MAINWP_BASE_URL: z.string().url().optional(),
  /** MainWP REST API v2 bearer key (Dashboard > API Access > API Keys, Read) */
  MAINWP_API_KEY: z.string().optional(),
  /**
   * Dedicated shared secret for the Ventin MainWP extension's action endpoint
   * (operator cache-clear). Separate from the read-only MAINWP_API_KEY (plan
   * D11). Optional: when unset the cache-clear route returns 503.
   */
  MAINWP_ACTION_SECRET: blankAsUndefined(z.string().min(16).optional()),
  SCHEDULER_SHARED_SECRET: blankAsUndefined(z.string().min(16).optional()),

  // Web origin (CORS)
  WEB_ORIGIN: z.string().url().default('http://localhost:5173'),

  // Application JWT secret (signs GA4 OAuth state payloads, etc.)
  // Required in production — set as an environment variable.
  // The GA4 OAuth callback cannot receive the app session cookie reliably
  // across provider redirects, so state is signed with this key.
  APP_JWT_SECRET: z.string().min(32, 'APP_JWT_SECRET must be at least 32 chars'),

  // Resend inbound webhook secret — used by apps/api/src/services/inboundEmailService.ts
  // to HMAC-SHA256 verify Resend inbound payloads. REQUIRED at boot so missing
  // configuration fails loudly instead of silently rejecting every inbound email.
  RESEND_WEBHOOK_SECRET: z
    .string()
    .min(1, 'RESEND_WEBHOOK_SECRET is required for Resend inbound webhook verification'),
  RESEND_API_KEY: blankAsUndefined(z.string().min(1).optional()),
  RESEND_FROM_EMAIL: blankAsUndefined(z.string().email().optional()),
});

export type Env = z.infer<typeof envSchema>;
